import {action, makeAutoObservable, makeObservable, observable, transaction, computed, runInAction, set} from "mobx";
import {interpret} from "xstate";
import {organizationStore, userStore} from "./StoreLoader";
import moment from "moment";
import {FetchMachine} from "../components/machines/FetchMachine";
import {getPlatform} from "../utils/StringUtilities";
import {sourceTypesEnum} from "../utils/SourceUtils";
import {isFacebookImageExpired} from "../utils/FacebookUtils";
import WatsonApi from "../backends/WatsonApi";
import {ExecutionEnvironment} from "./InterfaceStore";

interface IReactiveContentItem {
    update: (obj: { [v: string]: any }) => void,
    image: string,
}

export type IReactiveNewsContentItem = IReactiveContentItem & (NewsContentItem | MediaContentItem | FileContentItem);
export type IReactiveEventContentItem = IReactiveContentItem & EventContentItem;

export class ReactiveContentItem {
    // see https://github.com/mobxjs/mobx/issues/2486, should be able to remove this after upgrading to babel@7.12.0
    organization = undefined;

    constructor(item: NewsContentItem | MediaContentItem | FileContentItem | EventContentItem) {
        const obj = {
            update: action,
            image: computed,
        }
        for (const key in item) {
            if (item.hasOwnProperty(key)) {
                this[key] = item[key];
                obj[key] = observable;
            }
        }

        makeObservable(this, obj)
    }

    get image() {
        return getItemImage(this);
    }

    update(obj) {
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                this[key] = obj[key];
            }
        }
    }
}

function isNotEmpty(item) {
    return item.title || item.image || item.description;
}

function getItemImage(item) {
    const isImage = /photo/.test(item.type);
    const platform = getPlatform(item.type);

    let image = null;

    // first try to get the image from the content item json_data
    if (item.is_news && item.json_data.news.cover_image_url) {
        image = item.json_data.news.cover_image_url;
    } else if (item.is_event && item.json_data.event && item.json_data.event.full_picture) {
        image = item.json_data.event.full_picture;
    } else if (item.is_media && isImage && item.json_data.images?.length > 0) {
        image = item.json_data.images[item.json_data.images.length - 1].raw_url;
    } else if (item.is_media && item.json_data.videos) {
        image = item.json_data.videos.videos_list[0].raw_url;
    } else if (item.is_file && item.json_data.file.cover_image_url) {
        image = item.json_data.file.cover_image_url;
    }

    // make sure image URL isn't expired if it's from Facebook - if it is, we need to continue onward
    if (platform === sourceTypesEnum.FACEBOOK && isFacebookImageExpired(image)) {
        image = null;
    }

    // if we still don't have an image, try to extract from content source, making sure that if it's from Facebook it isn't expired
    if (
        !image &&
        item.source &&
        item.source.account_image &&
        !(
            platform === sourceTypesEnum.FACEBOOK &&
            isFacebookImageExpired(item.source.account_image)
        )
    ) {
        if (platform === sourceTypesEnum.TWITTER) {
            // use larger version of profile picture
            image = item.source.account_image.replace("_normal", "");
        } else {
            image = item.source.account_image;
        }
    }

    return image;
}

/*
switched from managing content in React state to a mobx store primarily because needed a way to easily observe and react to
item property changes - i.e. the published value. React doesn't provide a straightforward way to do that since object references
do not change. Also added benefit of being able to manage fetching and content from a single class instance rather than using
multiple states that need to be combined inside the component.
*/
export class SchoolFeedStore {
    news: IReactiveNewsContentItem[] = [];
    events: IReactiveEventContentItem[] = [];
    currentNewsPage = 0;
    currentEventsPage = 0;
    isMoreNews = true;
    isMoreEvents = true;
    fetchStatus = FetchMachine.initialState.value;

    service = interpret(FetchMachine).onTransition(current => {
        runInAction(() => this.fetchStatus = current.value);
    });

    constructor() {
        makeAutoObservable(this);

        if (ExecutionEnvironment.canUseDOM) {
            // this leaks memory, so very important this doesn't get run on the server!
            this.service.start();
        }
    }

    fetchNews = async (baseFetchQuery) => {
        this.currentNewsPage++;
        const {send} = this.service;

        try {
            send("FETCH");
            const q = {
                ...baseFetchQuery,
                is_news: true,
                is_media: true,
                is_file: true,
                page_size: 10,
                expand: "organization",
                page: this.currentNewsPage,
            };
            const client = await WatsonApi(organizationStore.currentOrganization.id, userStore.id, false);
            const obj = await client["content-items"].content_items_list(q);
            const response = JSON.parse(obj.data);
            transaction(action(() => {
                // filter out duplicates
                response.data.forEach(item => {
                    if (!this.news.find(n => {
                        return n.source_id !== item.source_id &&
                            n.title === item.title &&
                            n.description === item.description
                    })) {
                        const contentItem: unknown = new ReactiveContentItem(item);
                        if (isNotEmpty(contentItem)) {
                            this.news.push(contentItem as IReactiveNewsContentItem);
                        }
                    }
                })
                this.isMoreNews = !!response.links.next;
            }))
            send("FULFILL");
        } catch (e) {
            send("REJECT");
        }
    }

    fetchEvents = async (baseFetchQuery) => {
        this.currentEventsPage++;
        const {send} = this.service;

        try {
            send("FETCH");
            const q = {
                ...baseFetchQuery,
                is_event: true,
                page_size: 5,
                created_at__gte: moment().format('YYYY-MM-DD'),
                created_at__lte: moment().add(1, "week").add(1, "day").format('YYYY-MM-DD'),
                expand: "organization",
                page: this.currentEventsPage,
            };
            const client = await WatsonApi(organizationStore.currentOrganization.id, userStore.id, false);
            const obj = await client["content-items"].content_items_list(q);
            const response = JSON.parse(obj.data);
            transaction(action(() => {
                response.data.forEach(item => {
                    // filter out duplicates
                    if (!this.events.find(n => {
                        return n.source_id !== item.source_id &&
                            n.json_data.event.start === item.json_data.event.start &&
                            n.json_data.event.end === item.json_data.event.end &&
                            n.title === item.title &&
                            n.description === item.description
                    })) {
                        const contentItem: unknown = new ReactiveContentItem(item);
                        if (isNotEmpty(contentItem)) {
                            this.events.push(contentItem as IReactiveEventContentItem);
                        }
                    }
                })
                console.log('more events? ', !!response.links.next);
                this.isMoreEvents = !!response.links.next;
            }))
            send("FULFILL");
        } catch (e) {
            send("REJECT");
        }
    }

    fetchMore = async (baseFetchQuery) => {
        if (this.isMoreNews) await this.fetchNews(baseFetchQuery);
        if (this.isMoreEvents) await this.fetchEvents(baseFetchQuery);
    }

    toggleHideItem = async (item, organization_id) => {
        const client = await WatsonApi(organizationStore.currentOrganization.id, userStore.id, false);
        const result = await client.organizations.organizations_content_items_partial_update({
            id: item.id,
            organization_pk: organization_id,
            data: {"user_published": !item.published},
        });
        if (item.is_news || item.is_media || item.is_file) {
            const found = this.news.find(c => c.id === item.id);
            if (found) {
                found.update(result.obj);
            }
        } else if (item.is_event) {
            const found = this.events.find(c => c.id === item.id);
            if (found) {
                found.update(result.obj);
            }
        }
    }

    deleteItem = async (item, organization_id) => {
        const client = await WatsonApi(organizationStore.currentOrganization.id, userStore.id, false);
        const result = await client.organizations.organizations_content_items_delete({
            id: item.id,
            organization_pk: organization_id,
        });
        if (result && (item.is_news || item.is_media || item.is_file)) {
            this.news = this.news.filter(n => n.id !== item.id);
        } else if (result && item.is_event) {
            this.events = this.events.filter(n => n.id !== item.id);
        }
    }

    addNewsItem = item => {
        const contentItem: unknown = new ReactiveContentItem(item);
        this.news.unshift(contentItem as IReactiveNewsContentItem);
    }

    addEventItem = item => {
        const contentItem: unknown = new ReactiveContentItem(item);
        this.events.unshift(contentItem as IReactiveEventContentItem);
    }

    updateItem = (updatedItem) => {
        let itemToUpdate;
        if (updatedItem.is_event) {
            itemToUpdate = this.events.find(e => e.id === updatedItem.id);
        } else {
            itemToUpdate = this.news.find(n => n.id === updatedItem.id);
        }
        for (let k in updatedItem) {
            if (updatedItem.hasOwnProperty(k)) {
                set(itemToUpdate, k, updatedItem[k]);
            }
        }
    }

    get content() {
        const c: Array<IReactiveNewsContentItem | IReactiveEventContentItem> = [];
        let currentNewsIndex = 0;
        let currentEventsIndex = 0;
        for (let i = 0; i < this.news.length + this.events.length; i++) {
            if (i % 3 === 0 && currentEventsIndex < this.events.length) {
                c.push(this.events[currentEventsIndex]);
                currentEventsIndex++;
            } else if (currentNewsIndex < this.news.length) {
                c.push(this.news[currentNewsIndex]);
                currentNewsIndex++;
            }
        }
        return c;
    }

    get isFetching(): boolean {
        return this.fetchStatus === "PENDING";
    }

    get isMore(): boolean {
        return this.isMoreNews || this.isMoreEvents;
    }
}
